// apps/web/src/lib/reminderScheduler.ts
// Turns the suppression engine's decisions into actual local notifications.
//
// Everything is scheduled on-device: no cron, no per-timezone fan-out, works
// with no network, and the server never learns your meal times. The cost is
// that a notification scheduled at 09:00 cannot know you logged lunch at
// 13:10, so the answer is to throw the schedule away and rebuild it whenever
// the day's state could have changed — on foreground, and after every write.
// Rebuilding is what makes "already done → say nothing" true in practice
// rather than only in the engine.
import { isNativeApp } from "./native";
import {
  planReminders, REMINDER_KINDS, DEFAULT_QUIET_FROM, DEFAULT_QUIET_TO, DEFAULT_TIMES,
  type ReminderKind, type ReminderPrefs, type DayState,
} from "@shared/notifications";
import type { TodayResponse } from "@shared/types";

/** Stable per kind so a reschedule replaces rather than duplicates. Well clear
 *  of the legacy weigh-in id (1001) that lib/push.ts still owns. */
const NOTIFICATION_IDS: Record<ReminderKind, number> = {
  weigh_in: 2001,
  meal_lunch: 2002,
  meal_dinner: 2003,
  water: 2004,
  day_wrap: 2005,
  check_in_ready: 2006, encourage: 2007,
};
const ID_TO_KIND = new Map(Object.entries(NOTIFICATION_IDS).map(([k, id]) => [id, k as ReminderKind]));

const SENT_KEY = "caloly.reminders.sent";

interface SentRecord { date: string; kinds: ReminderKind[] }

function readSent(today: string): ReminderKind[] {
  try {
    const r = JSON.parse(localStorage.getItem(SENT_KEY) || "null") as SentRecord | null;
    return r && r.date === today ? r.kinds : [];
  } catch { return []; }
}

function writeSent(today: string, kinds: ReminderKind[]): void {
  try {
    localStorage.setItem(SENT_KEY, JSON.stringify({ date: today, kinds: [...new Set(kinds)] } satisfies SentRecord));
  } catch { /* storage unavailable */ }
}

/** What the day looks like to the engine. Derived from the payload Today
 *  already has, so checking costs no extra request. */
export function dayStateFrom(today: TodayResponse): DayState {
  const slots = new Set(today.entries.map((e) => e.plan_meal_slot).filter(Boolean));
  return {
    weight_logged: today.weighed_today,
    slots_logged: [...slots] as DayState["slots_logged"],
    water_ml: today.consumed_water_ml,
    water_target_ml: today.goals.water_target_ml ?? 0,
    any_entry: today.entries.length > 0,
    check_in_ready: today.pending_check_in !== null,
  };
}

async function notifications() {
  // Module, not proxy — returning the plugin proxy from an async function
  // deadlocks the caller's await (its `.then` probe becomes a native call
  // that never settles). See push.ts nativeNotifications.
  return await import("@capacitor/local-notifications");
}

/**
 * Fold anything iOS has actually shown into today's delivered set.
 *
 * The app is not running when a local notification fires, so this is the only
 * way to know one went out — and without it the two-a-day cap would reset every
 * time the app was killed, which is the same as having no cap.
 */
async function reconcileDelivered(todayDate: string): Promise<ReminderKind[]> {
  const sent = new Set(readSent(todayDate));
  try {
    const { LocalNotifications: ln } = await notifications();
    const { notifications: delivered } = await ln.getDeliveredNotifications();
    for (const n of delivered ?? []) {
      const kind = ID_TO_KIND.get(n.id);
      if (kind) sent.add(kind);
    }
  } catch { /* not available on this platform — the stored set still stands */ }
  const kinds = [...sent];
  writeSent(todayDate, kinds);
  return kinds;
}

export interface ReminderTexts {
  /** Copy per kind. Lives in the locale files, never here — the wording rules
   *  (no streaks, no days missed, never about what was eaten) are the reason
   *  this module does not get to invent a single string. */
  title: (kind: ReminderKind) => string;
  body: (kind: ReminderKind) => string;
}

/**
 * Rebuild today's schedule from the current state. Safe to call often — it
 * cancels everything it owns first, so it converges rather than accumulating.
 */
export async function syncReminders(
  today: TodayResponse,
  prefs: ReminderPrefs,
  texts: ReminderTexts,
): Promise<void> {
  if (!isNativeApp()) return;

  const now = new Date();
  const nowMinutes = now.getHours() * 60 + now.getMinutes();
  const sentToday = await reconcileDelivered(today.date);

  const plan = planReminders({
    nowMinutes,
    prefs,
    state: dayStateFrom(today),
    sentToday,
  });

  const { LocalNotifications: ln } = await notifications();
  // Cancel every kind, not only the ones being replaced: a reminder whose task
  // was completed since the last sync has to disappear, and it is only
  // identifiable by having dropped out of the plan.
  await ln.cancel({ notifications: REMINDER_KINDS.map((k) => ({ id: NOTIFICATION_IDS[k] })) })
    .catch(() => { /* nothing scheduled yet */ });
  if (plan.length === 0) return;

  await ln.schedule({
    notifications: plan.map(({ kind, at }) => ({
      id: NOTIFICATION_IDS[kind],
      title: texts.title(kind),
      body: texts.body(kind),
      schedule: {
        at: new Date(now.getFullYear(), now.getMonth(), now.getDate(), Math.floor(at / 60), at % 60),
        allowWhileIdle: true,
      },
      extra: { kind },
    })),
  });
}

/** Cancel everything this module owns — used when reminders are switched off
 *  wholesale, and on logout. */
export async function cancelAllReminders(): Promise<void> {
  if (!isNativeApp()) return;
  try {
    const { LocalNotifications: ln } = await notifications();
    await ln.cancel({ notifications: REMINDER_KINDS.map((k) => ({ id: NOTIFICATION_IDS[k] })) });
  } catch { /* nothing scheduled */ }
  try { localStorage.removeItem(SENT_KEY); } catch { /* storage unavailable */ }
}

/** Shape the API's reminder response into what the engine expects. */
export function prefsFromApi(r: {
  master: boolean; quiet_from: string; quiet_to: string;
  settings: { kind: string; enabled: boolean; local_time: string }[];
}): ReminderPrefs {
  const kinds: ReminderPrefs["kinds"] = {};
  for (const s of r.settings) {
    const kind = REMINDER_KINDS.find((k) => k === s.kind);
    if (kind) kinds[kind] = { enabled: s.enabled, local_time: s.local_time || DEFAULT_TIMES[kind] };
  }
  return {
    master: r.master,
    quiet_from: r.quiet_from || DEFAULT_QUIET_FROM,
    quiet_to: r.quiet_to || DEFAULT_QUIET_TO,
    kinds,
  };
}
